/* Notification Center — the Delivery Method fields, shared by Rule Detail → Delivery Method and the
 * New Rule wizard's Delivery Method step (owner request 2026-10-21), so the two can never drift.
 *
 * Controlled: the caller owns the value and saves it. In-App / Email, the Pop-up setting, Requires
 * acknowledgment, Repeat until acknowledged, Delay, Business hours and Auto-resolve, Suppress duplicate firing and Client visible (portal) are SAVED on the rule. Dashboard /
 * SMS / Teams have no sender yet, so they are shown greyed and unticked ("Not available yet") — never
 * ticked, which would claim a channel that is not on. The server re-checks every saved value (DeliveryScheduler /
 * RepeatPolicy), so the rules here only guide the user. */
import { useEffect, useState } from 'react';
import { describeAlertPopup, type AlertPopupSettings } from './inbox/alertPopupSettings';
import { listPopupSettings, type PopupSetting } from './inbox/inboxApi';
import { EscalationFields } from './EscalationFields';
import { escAvailable, type EscalationValue } from './escalation';
import {
  REPEAT_UNITS, bizHoursLabel, delayNote, effectiveAck, repeatIntervalError, repeatOffReason,
  type DelayLabel, type DeliveryValue, type RepeatUnit,
} from './deliveryMethod';

/** Hours ⇄ Minutes spinner — the Hold Time Warn Before spinner's look (.nc-wb-spin): ▲/▼ buttons or ↑/↓ keys. */
function UnitSpin({ value, onChange, disabled }: { value: RepeatUnit; onChange: (u: RepeatUnit) => void; disabled?: boolean }) {
  const step = (dir: 1 | -1) => {
    if (disabled) return;
    const i = REPEAT_UNITS.indexOf(value);
    onChange(REPEAT_UNITS[(i + dir + REPEAT_UNITS.length) % REPEAT_UNITS.length]!);
  };
  return (
    <div className={`nc-wb-spin nc-repeat-spin${disabled ? ' is-disabled' : ''}`} role="spinbutton" tabIndex={disabled ? -1 : 0}
      aria-label="Repeat unit" aria-valuetext={value} aria-valuenow={REPEAT_UNITS.indexOf(value)}
      aria-valuemin={0} aria-valuemax={REPEAT_UNITS.length - 1} aria-disabled={disabled || undefined}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp') { e.preventDefault(); step(1); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); step(-1); }
      }}>
      <span className="nc-wb-unit">{value}</span>
      <span className="nc-wb-spin-btns">
        <button type="button" tabIndex={-1} aria-label="Next repeat unit" disabled={disabled} onClick={() => step(1)}>▲</button>
        <button type="button" tabIndex={-1} aria-label="Previous repeat unit" disabled={disabled} onClick={() => step(-1)}>▼</button>
      </span>
    </div>
  );
}

/** A channel nothing sends yet: always greyed and unticked, with the reason as its tooltip. */
const NOT_AVAILABLE = 'Not available yet — nothing sends this channel, so it cannot be turned on.';
function ComingSoon({ label }: { label: string }) {
  return (
    <label className="nc-check is-disabled" title={NOT_AVAILABLE}>
      <input type="checkbox" checked={false} disabled readOnly /> {label}
      <span className="nc-na">(not available yet)</span>
    </label>
  );
}

function ToggleRow({ text, sub, on, onClick, disabled, title }: { text: string; sub: string; on: boolean; onClick: () => void; disabled?: boolean; title?: string }) {
  return <div className="nc-togrow" title={title}><div><div className="nc-togrow-text">{text}</div><div className="nc-togrow-sub">{sub}</div></div><span className={`nc-toggle${on ? ' on' : ''}${disabled ? ' nc-ro' : ''}`} role="button" aria-label={text} aria-disabled={disabled || undefined} onClick={disabled ? undefined : onClick} /></div>;
}

/** The Delivery Method fields. Wrap them in an element carrying `nc-delivery-panel` so the toggle
 *  descriptions run the full width. */
export function DeliveryMethodFields({ value, onChange, disabled = false, showSuppress, showPortal = true, escalation }: {
  value: DeliveryValue;
  onChange: (next: DeliveryValue) => void;
  /** Read-only (Rule Detail in View mode). */
  disabled?: boolean;
  /** Suppress duplicate firing is offered only for permit-limit exceedances (PERMIT_EXCEEDANCE_CATEGORY_CODE). */
  showSuppress: boolean;
  /** Client visible (portal) is hidden for Hold Time (RC-02) rules — an internal lab deadline the client portal never
   *  shows (owner request 2026-10-23). Hidden, its stored value is kept unchanged. */
  showPortal?: boolean;
  /** Escalation (AI_NotificationRule.EscalationGroupId / EscalateAfter), shown inside the In-App alert group because
   *  it needs In-App + Requires acknowledgment and an acknowledgment stops it (owner request 2026-10-23). */
  escalation?: { value: EscalationValue; onChange: (next: EscalationValue) => void };
}) {
  const v = value;
  const set = (patch: Partial<DeliveryValue>) => onChange({ ...v, ...patch });
  const setRepeat = (patch: Partial<DeliveryValue['repeat']>) => set({ repeat: { ...v.repeat, ...patch } });

  // The named settings from Notification Center → Pop-up Settings. Quiet: the dropdown just falls back to
  // the built-in look when the list cannot be loaded.
  const [popupList, setPopupList] = useState<PopupSetting[]>([]);
  useEffect(() => {
    let live = true;
    listPopupSettings().then((l) => { if (live) setPopupList(l); }).catch(() => { if (live) setPopupList([]); });
    return () => { live = false; };
  }, []);
  const popupDefault = popupList.find((p) => p.isDefault);
  /** What this rule's pop-up will actually do: its chosen setting if Active, else the Default. */
  const popupEffective: (AlertPopupSettings & { name: string }) | undefined =
    (v.popupSetting ? popupList.find((p) => p.id === v.popupSetting && p.isActive) : undefined) ?? popupDefault;

  const repeatOff = repeatOffReason(v);
  const repeatInvalid = v.repeat.on && !repeatOff && !!repeatIntervalError(v.repeat);

  return (
    <>
      <div className="nc-form-group">
        <label className="nc-flabel">Notification type</label>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {/* Dashboard is not available yet (owner decision 2026-10-23): greyed and unticked. */}
          <ComingSoon label="Dashboard" />
          {/* In-App + Email are SAVED and drive the dispatcher. */}
          <label className="nc-check"><input type="checkbox" checked={v.inApp} disabled={disabled}
            onChange={(e) => set(e.target.checked ? { inApp: true } : { inApp: false, ack: false })} /> In-App (bell)</label>
          <label className="nc-check"><input type="checkbox" checked={v.email} disabled={disabled}
            onChange={(e) => set({ email: e.target.checked })} /> Email</label>
          <ComingSoon label="SMS" />
          <ComingSoon label="Teams" />
        </div>
        {!v.inApp && !v.email && (
          <div className="nc-panel-sub" style={{ color: 'var(--ai-notify-error)', marginTop: 6 }}>
            Neither In-App nor Email is ticked — this rule will record alerts but send nothing.
          </div>
        )}
      </div>
      {/* In-App alert group (owner request 2026-10-18): the settings that only affect the In-App alert sit
          together, are always shown, and are enabled only while In-App (bell) is ticked. */}
      <div className={`nc-inapp-group${v.inApp ? '' : ' is-off'}`} role="group" aria-label="In-App alert">
        <div className="nc-inapp-hd">
          <i className="ti ti-bell" /> In-App alert
          {!v.inApp && <span className="nc-inapp-off">Turn on In-App (bell) above to use these settings.</span>}
        </div>
        <div className="nc-form-group nc-inapp-popup">
          <label className="nc-flabel" htmlFor="nc-popup-setting">Pop-up setting</label>
          {/* Compact dropdown with its description beside it on the same line (owner request 2026-10-18). */}
          <div className="nc-inapp-popup-row">
            <select id="nc-popup-setting" className="nc-fselect nc-inapp-popup-select" value={v.popupSetting} disabled={disabled || !v.inApp}
              title={v.inApp ? undefined : 'Needs In-App (bell) — the pop-up setting is only for In-App alerts.'}
              onChange={(e) => set({ popupSetting: e.target.value })}>
              {/* '' = follow the Default setting — shown by its own name (no "Default" prefix, owner request
                  2026-10-18); the built-in look when no Default exists. */}
              <option value="">{popupDefault ? popupDefault.name : 'Built-in (bottom right · auto-close 8 s)'}</option>
              {popupList.filter((p) => !p.isDefault && (p.isActive || p.id === v.popupSetting)).map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.isActive ? '' : ' (inactive)'}</option>
              ))}
            </select>
            <div className="nc-panel-sub nc-inapp-popup-desc">
              <i className="ti ti-bell-cog" /> {popupEffective ? describeAlertPopup(popupEffective) : 'Bottom right · auto-close 8 s'}
              {effectiveAck(v.inApp, v.ack) && ' · this rule is ACK REQUIRED, so it always needs Acknowledge'}
            </div>
          </div>
        </div>
        {/* Requires acknowledgment (AI_NotificationRule.Ack) — it only changes how the In-App alert behaves. */}
        <ToggleRow text="Requires acknowledgment" sub={v.inApp
            ? 'In-App alerts from this rule stay on screen until someone clicks Acknowledge, pop up again at sign-in until acknowledged, and each acknowledgment is recorded in the audit trail. Emails are not affected.'
            : 'Needs In-App (bell) ticked above — acknowledgment is only for In-App alerts. Emails cannot be acknowledged.'}
          on={effectiveAck(v.inApp, v.ack)} onClick={() => set({ ack: !v.ack })} disabled={disabled || !v.inApp}
          title={v.inApp ? undefined : 'Needs In-App (bell) — acknowledgment is only for In-App alerts.'} />
        {/* Repeat every N Hours/Minutes until acknowledged — enabled only with In-App + Requires
            acknowledgment (the acknowledgment stops it) and not with a Daily digest delay. */}
        <div className={`nc-repeat${repeatOff ? ' is-off' : ''}`} title={repeatOff ?? undefined}>
          <div className="nc-repeat-row">
            <label className="nc-check nc-repeat-check">
              <input type="checkbox" checked={v.repeat.on} disabled={disabled || !!repeatOff}
                onChange={(e) => setRepeat({ on: e.target.checked })} />
              Repeat every
            </label>
            <input className={`nc-finput nc-repeat-num${repeatInvalid ? ' invalid' : ''}`} inputMode="numeric" maxLength={3}
              aria-label="Repeat interval" aria-invalid={repeatInvalid || undefined}
              value={v.repeat.value} disabled={disabled || !!repeatOff || !v.repeat.on}
              onChange={(e) => { if (/^\d*$/.test(e.target.value)) setRepeat({ value: e.target.value }); }} />
            <UnitSpin value={v.repeat.unit} disabled={disabled || !!repeatOff || !v.repeat.on}
              onChange={(u) => setRepeat({ unit: u })} />
            <span className="nc-repeat-tail">until acknowledged</span>
          </div>
          <div className="nc-togrow-sub">{repeatOff
            ?? 'Re-sends the alert as “Reminder N” (In-App, and Email when ticked) at this interval until anyone acknowledges it in the bell or it auto-resolves.'}</div>
          {repeatInvalid && <div className="nc-repeat-err" role="alert">{repeatIntervalError(v.repeat)}</div>}
        </div>
        {/* Escalation is shown only when it can work — In-App + Requires acknowledgment, no Daily digest — as one
            sentence (owner request 2026-10-24): no greyed fields, no "Needs … ticked above" lines. */}
        {escalation && escAvailable({ inApp: v.inApp, ack: effectiveAck(v.inApp, v.ack), delay: v.delay }) && (
          <div className="nc-inapp-esc" role="group" aria-label="Escalation">
            <EscalationFields value={escalation.value} onChange={escalation.onChange} disabled={disabled}
              delivery={{ email: v.email, bizHours: v.bizHours }} />
          </div>
        )}
      </div>
      <div className="nc-form-row" style={{ marginBottom: 16 }}>
        <div className="nc-form-group" style={{ marginBottom: 0 }}>
          <label className="nc-flabel" htmlFor="nc-delay">Delay</label>
          <select id="nc-delay" className="nc-fselect" disabled={disabled} value={v.delay} onChange={(e) => set({ delay: e.target.value as DelayLabel })}>
            <option>Immediate</option><option>After 1 hour</option><option>Daily digest</option>
          </select>
        </div>
        <div className="nc-form-group" style={{ marginBottom: 0 }}>
          <label className="nc-flabel" htmlFor="nc-bizhours">Business hours</label>
          <select id="nc-bizhours" className="nc-fselect" disabled={disabled} value={v.bizHours ? 'only' : 'any'} onChange={(e) => set({ bizHours: e.target.value === 'only' })}>
            <option value="any">{bizHoursLabel(false)}</option><option value="only">{bizHoursLabel(true)}</option>
          </select>
        </div>
      </div>
      <div className="nc-panel-sub nc-delay-note">{delayNote(v.delay, v.bizHours)}</div>
      {/* Permit-limit exceedances only (category RC-01): a hold-time threshold is one occurrence by design. */}
      {showSuppress && (
        <ToggleRow text="Suppress duplicate firing" sub="Re-occurrence on the same source record increments a repeat counter (×N) instead of sending again." on={v.suppress} onClick={() => set({ suppress: !v.suppress })} disabled={disabled} />
      )}
      {/* Bypass digest batching was removed 2026-10-24 (owner decision): Delay alone decides the timing. */}
      {showPortal && <ToggleRow text="Client visible (portal)" sub="Whether the client portal can see this notification. Off for internal-only alerts." on={v.portal} onClick={() => set({ portal: !v.portal })} disabled={disabled} />}
      <ToggleRow text="Auto-resolve when condition clears" sub="When everything the alert covers no longer meets its condition (a permit result back inside the limit or rolled back; a hold-time test prepped or analysed), the alert is marked Resolved: it stops waiting for acknowledgment and the Delivery Log shows when." on={v.autoResolve} onClick={() => set({ autoResolve: !v.autoResolve })} disabled={disabled} />
    </>
  );
}
